import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Plus, Trash2, Search, AlertTriangle, X, Save, Edit3, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { WarrantiesPanel } from "../components/WarrantiesPanel";

const fmtKc = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";

const STATUS = {
    reported: { label: "Nahlášeno", color: "bg-red-100 text-red-700 border-red-300" },
    in_progress: { label: "V řešení", color: "bg-orange-100 text-orange-700 border-orange-300" },
    resolved: { label: "Vyřešeno", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
    rejected: { label: "Zamítnuto", color: "bg-slate-200 text-slate-600 border-slate-400" },
};

const EMPTY = {
    job_id: "",
    reported_date: new Date().toISOString().slice(0, 10),
    description: "",
    cost: 0,
    status: "reported",
    resolution_date: "",
    notes: "",
};

export default function Reklamace() {
    const [items, setItems] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [summary, setSummary] = useState(null);
    const [view, setView] = useState("claims");
    const [q, setQ] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        api.get("/claims").then((r) => { setItems(r.data); setLoading(false); });
        api.get("/claims/summary").then((r) => setSummary(r.data));
    };

    useEffect(() => {
        load();
        api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    }, []);

    const save = async () => {
        if (!editing.job_id) { toast.error("Vyberte zakázku"); return; }
        if (!editing.description?.trim()) { toast.error("Vyplňte popis reklamace"); return; }
        try {
            const payload = { ...editing, cost: Number(editing.cost) || 0 };
            if (!payload.resolution_date) delete payload.resolution_date;
            if (editing.id) await api.put(`/claims/${editing.id}`, payload);
            else await api.post("/claims", payload);
            toast.success("Uloženo");
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const del = async (id) => {
        if (!window.confirm("Opravdu smazat tuto reklamaci?")) return;
        try { await api.delete(`/claims/${id}`); toast.success("Smazáno"); load(); }
        catch { toast.error("Smazání selhalo"); }
    };

    const filtered = useMemo(() => items.filter((c) => {
        if (filterStatus && c.status !== filterStatus) return false;
        const s = q.toLowerCase();
        return !q || (c.description + " " + (c.job_title || "") + " " + (c.client_name || "")).toLowerCase().includes(s);
    }), [items, q, filterStatus]);

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="claims-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Záruční servis</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Reklamace</h1>
                    <p className="text-sm text-slate-600 mt-2">Evidence reklamací v záruce, náklady na opravy, stav řešení.</p>
                </div>
                <div className="flex gap-1 border border-slate-300 p-0.5 bg-white" data-testid="claims-view-switch">
                    {[["claims", "Reklamace"], ["warranties", "Záruky"]].map(([k, l]) => <button key={k} onClick={() => setView(k)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold ${view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`claims-view-${k}`}>{l}</button>)}
                </div>
                {view === "claims" && <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-claim-button"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Nová reklamace
                </button>}
            </div>
            {view === "warranties" && <WarrantiesPanel />}
            {view === "claims" && <>

            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
                    <StatCard label="Celkem" value={String(summary.total_count)} testid="stat-total" />
                    <StatCard label="Nahlášeno" value={String(summary.by_status?.reported?.count || 0)} testid="stat-reported" highlight={(summary.by_status?.reported?.count || 0) > 0} />
                    <StatCard label="V řešení" value={String(summary.by_status?.in_progress?.count || 0)} testid="stat-in-progress" />
                    <StatCard label="Vyřešeno" value={String(summary.by_status?.resolved?.count || 0)} testid="stat-resolved" />
                    <StatCard label="Náklady celkem" value={fmtKc(summary.total_cost)} testid="stat-cost" gold />
                </div>
            )}

            <div className="bg-white border border-slate-200">
                <div className="flex items-center px-4 py-3 border-b border-slate-200 gap-3">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input placeholder="Hledat…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="claims-search"
                           className="flex-1 text-sm focus:outline-none bg-transparent" />
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status"
                            className="text-xs uppercase tracking-wider border border-slate-300 px-2 py-1.5 rounded-sm">
                        <option value="">Všechny stavy</option>
                        <option value="reported">Nahlášeno</option>
                        <option value="in_progress">V řešení</option>
                        <option value="resolved">Vyřešeno</option>
                        <option value="rejected">Zamítnuto</option>
                    </select>
                    <span className="text-xs text-slate-400">{filtered.length} / {items.length}</span>
                </div>
                {loading ? (
                    <div className="p-12 text-center text-slate-400 text-xs uppercase tracking-widest">Načítání…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">{items.length === 0 ? "Žádná reklamace" : "Nic nenalezeno"}</div>
                        <p className="text-sm text-slate-500 mt-1">{items.length === 0 ? "Ať to tak zůstane 🤞" : "Zkuste jiné hledání."}</p>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                <th className="px-4 py-3 font-semibold">Nahlášeno</th>
                                <th className="px-4 py-3 font-semibold">Zakázka / Klient</th>
                                <th className="px-4 py-3 font-semibold">Popis</th>
                                <th className="px-4 py-3 font-semibold">Stav</th>
                                <th className="px-4 py-3 font-semibold text-right">Náklad</th>
                                <th className="px-4 py-3 font-semibold">Vyřešeno</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {filtered.map((c, i) => {
                                const st = STATUS[c.status] || {};
                                return (
                                    <tr key={c.id} className={`border-b border-slate-100 hover:bg-orange-50/40 ${i % 2 ? "bg-slate-50/40" : ""}`} data-testid={`claim-row-${c.id}`}>
                                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{c.reported_date}</td>
                                        <td className="px-4 py-3">
                                            <Link to={`/zakazky/${c.job_id}`} className="font-semibold text-slate-900 hover:text-orange-700 inline-flex items-center gap-1">
                                                {c.job_title || "(neznámá)"} <ExternalLink className="w-3 h-3 text-slate-400" />
                                            </Link>
                                            <div className="text-xs text-slate-500">{c.client_name}</div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-700 max-w-md">
                                            <div className="line-clamp-2">{c.description}</div>
                                            {c.notes && <div className="text-xs text-slate-500 italic mt-0.5 line-clamp-1">{c.notes}</div>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${st.color || "bg-slate-100 border-slate-300"}`}>{st.label || c.status}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtKc(c.cost)}</td>
                                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{c.resolution_date || "—"}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => setEditing({ ...c })} data-testid={`edit-claim-${c.id}`}
                                                        className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm">
                                                    <Edit3 className="w-3.5 h-3.5" />
                                                </button>
                                                <button onClick={() => del(c.id)} data-testid={`delete-claim-${c.id}`}
                                                        className="p-1.5 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-sm">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="claim-modal" onClick={() => setEditing(null)}>
                    <div className="bg-white border border-slate-200 shadow-2xl max-w-2xl w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                            <div>
                                <div className="label-eyebrow mb-1">{editing.id ? "Úprava" : "Nová reklamace"}</div>
                                <h2 className="font-display text-xl font-black tracking-tight text-slate-900">Reklamace v záruce</h2>
                            </div>
                            <button onClick={() => setEditing(null)} data-testid="claim-modal-close" className="text-slate-400 hover:text-slate-900">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-6 py-5 grid grid-cols-2 gap-4">
                            <F label="Zakázka *" className="col-span-2">
                                <select value={editing.job_id || ""} onChange={(e) => setEditing({ ...editing, job_id: e.target.value })} data-testid="claim-job-select"
                                        className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600">
                                    <option value="">— Vyberte zakázku —</option>
                                    {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}{j.client_name ? ` — ${j.client_name}` : ""}</option>)}
                                </select>
                            </F>
                            <F label="Datum nahlášení">
                                <input type="date" value={editing.reported_date} onChange={(e) => setEditing({ ...editing, reported_date: e.target.value })} data-testid="claim-date"
                                       className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            </F>
                            <F label="Stav">
                                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="claim-status"
                                        className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600">
                                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </F>
                            <F label="Popis reklamace *" className="col-span-2">
                                <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="claim-description"
                                          placeholder="Např. Praskla spára v koupelně u vany..."
                                          className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600 resize-none" />
                            </F>
                            <F label="Náklad na opravu (Kč)">
                                <input type="number" step="1" min="0" value={editing.cost} onChange={(e) => setEditing({ ...editing, cost: e.target.value })} data-testid="claim-cost"
                                       className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            </F>
                            <F label="Datum vyřešení">
                                <input type="date" value={editing.resolution_date || ""} onChange={(e) => setEditing({ ...editing, resolution_date: e.target.value })} data-testid="claim-resolution-date"
                                       className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            </F>
                            <F label="Poznámka" className="col-span-2">
                                <textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="claim-notes"
                                          className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600 resize-none" />
                            </F>
                        </div>
                        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm font-semibold uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm">
                                Zrušit
                            </button>
                            <button onClick={save} data-testid="claim-save-button"
                                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 text-sm font-semibold uppercase tracking-wider rounded-sm">
                                <Save className="w-4 h-4" /> Uložit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </>}
        </div>
    );
}

function StatCard({ label, value, testid, gold, highlight }) {
    const border = gold ? "border-orange-600" : highlight ? "border-red-400" : "border-slate-200";
    const valueColor = gold ? "text-orange-700" : highlight ? "text-red-700" : "text-slate-900";
    return (
        <div className={`bg-white border-l-4 ${border} border-t border-r border-b border-slate-200 px-4 py-3`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{label}</div>
            <div className={`font-display text-xl font-black tracking-tight ${valueColor}`}>{value}</div>
        </div>
    );
}

function F({ label, children, className = "" }) {
    return (
        <div className={className}>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">{label}</label>
            {children}
        </div>
    );
}
